import React, { useState } from "react";
import { Box, Stack } from "@mui/material";
import { AxiosError } from "axios";
import { Form, Formik, FormikHelpers, FormikValues } from "formik";
import { useBoundStore } from "../../../store";
import {
  LoginFormInitialValues as initValues,
  loginSchema,
} from "./constants";
import { IconCloseEye, IconEye } from "../../../utils/icons";
import { FieldText, FormButton } from ".";
import { LastUsedBadge } from "../../../components/auth/LastUsedBadge";

interface Props {
  onSuccess: () => void;
}

// The shared email/password login form (extracted from LoginPage so the RMN
// branded login can reuse the exact same auth flow with a different shell).
export function LoginForm({ onSuccess }: Props) {
  const login = useBoundStore((state) => state.login);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (
    values: FormikValues,
    helpers: FormikHelpers<typeof initValues>
  ) => {
    helpers.setSubmitting(true);
    try {
      await login(values.email, values.password);
      onSuccess();
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        const { data } = axiosError.response;
        if (data.error === "LoginCredentialsIncorrect") {
          helpers.setErrors({
            form: "Incorrect username or password. Please try again.",
          });
        } else {
          helpers.setErrors({ form: data.error });
        }
      }
    }
    helpers.setSubmitting(false);
  };

  return (
    <Formik
      initialValues={initValues}
      validationSchema={loginSchema}
      validateOnChange={false}
      validateOnMount={false}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form>
          <Stack spacing={5} width={1}>
            <Stack spacing={2} marginBottom={5}>
              <FieldText id="email" name="email" placeholder="Email" fullWidth />
              <Box position="relative">
                <FieldText
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  fullWidth
                  inputProps={{ paddingRight: 5 }}
                />
                <Box
                  position="absolute"
                  sx={{ right: 10, top: 12, cursor: "pointer" }}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <IconCloseEye />
                  ) : (
                    <IconEye stroke="#868686" />
                  )}
                </Box>
              </Box>
            </Stack>
            <Box position="relative">
              <FormButton
                text="Login"
                isLoadingBtn
                loading={isSubmitting}
              />
              <LastUsedBadge method="password" />
            </Box>
          </Stack>
        </Form>
      )}
    </Formik>
  );
}
